import {
  type EntityType,
  serializeMention,
} from '@/lib/crm/record/notes/mentionUtils';

export interface ChatAboutNavParams {
  orgId: number;
  entityId: string;
  entityType: string;
  displayName: string;
}

export interface ChatAboutNavState {
  pathname: string;
  state: {
    initialPrompt: string;
    fromConversationsHome: true;
    entityIds?: string[];
  };
}

/**
 * Serialize one record into the `<Entity>` mention token understood by the
 * Plate conversation editor.
 *
 * Delegates to {@link serializeMention} — the single source of truth for
 * mention markup — rather than re-implementing the token format and HTML
 * escaping. `serializeMention` normalizes the entity type to its canonical
 * `ahoy_*` form and escapes user-controlled values. `entityType` arrives as a
 * plain string (from `entity_type_name`); the cast is safe because
 * `serializeMention` normalizes any string internally via `normalizeEntityType`.
 */
function entityMentionToken(
  entityType: string,
  entityId: string,
  displayName: string
): string {
  return serializeMention(entityId, displayName, entityType as EntityType);
}

export function buildChatAboutNavState(
  params: ChatAboutNavParams
): ChatAboutNavState {
  const { orgId, entityId, entityType, displayName } = params;
  return {
    pathname: `/${orgId}/conversations`,
    state: {
      initialPrompt: `${entityMentionToken(entityType, entityId, displayName)} `,
      fromConversationsHome: true,
      entityIds: [entityId],
    },
  };
}

/**
 * Maximum number of records that can be chatted about in one go. Keeps the
 * seed prompt (and the agent's resulting context) bounded; larger selections
 * are rejected by the caller rather than truncated silently.
 */
export const MAX_CHAT_ABOUT_RECORDS = 25;

/** A single record to seed a "chat about these records" conversation with. */
export interface ChatAboutEntity {
  entityId: string;
  entityType: string;
  displayName: string;
}

export interface ChatAboutManyNavParams {
  orgId: number;
  entities: ChatAboutEntity[];
}

/**
 * Build the navigation state for chatting about several records at once.
 *
 * Mirrors {@link buildChatAboutNavState} but seeds the composer with one
 * `<Entity>` mention token per record (space-separated). The Plate conversation
 * editor renders each token as an inline entity badge, landing the user with
 * all records referenced and the cursor ready for their question.
 *
 * `entityIds` is forwarded alongside the prompt so the backend can
 * pre-register the records in the ReferenceMap and inject their summaries
 * into the agent's initial context without the agent making N separate
 * `get_crm_object` calls.
 */
export function buildChatAboutManyNavState(
  params: ChatAboutManyNavParams
): ChatAboutNavState {
  const { orgId, entities } = params;
  const mentions = entities
    .map(entity =>
      entityMentionToken(entity.entityType, entity.entityId, entity.displayName)
    )
    .join(' ');
  return {
    pathname: `/${orgId}/conversations`,
    state: {
      initialPrompt: mentions ? `${mentions} ` : '',
      fromConversationsHome: true,
      entityIds: entities.map(e => e.entityId),
    },
  };
}
